'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'

import { configPublic } from '@src/config.public'
import { useCart } from '@src/hooks/useCart'
import { useInterval } from '@src/hooks/useInterval'
import { useOrderContext } from '@src/hooks/useOrderContext'
import { usePageVisibility } from '@src/hooks/usePageVisibility'
import { getUnlocalizedPath } from '@src/i18n/locale.util'
import { ServerActions } from '@src/services/server-actions.service'
import { useIdentify, useMarketingAttribution } from '@src/utils/analytics.util'
import { COOKIES, ONE_DAY_IN_SECONDS, QUERY_PARAMS } from '@src/utils/constants.util'
import { getBrowserCookie, removeBrowserCookie, setBrowserCookie } from '@src/utils/cookies.util'
import { log } from '@src/utils/log.util'
import { WebSourceCodesSet } from '@src/utils/web-source-code.util'

export const initialState = {
  affiliateClickId: '' as string | undefined,
  setAffiliateClickId: (_affiliateClickId: string) => {},
  socialladderAmbassadorId: '' as string | undefined,
  setSocialladderAmbassadorId: (_socialladderAmbassadorId: string) => {},
  royaltyCode: '',
  setRoyaltyCode: (_royaltyCode: string) => {},
  webSourceCode: '',
  setWebSourceCode: (_webSourceCode: string) => {},
  isRecaptchaEnabled: false,
  setIsRecaptchaEnabled: (_isRecaptchaEnabled: boolean) => {},
}

export const TrackingCodesContext = createContext(initialState)

const logNampsace = '[TrackingCodes]'

/**
 * Manages different tracking codes in the app.
 * The codes are ingested from Builder page fields, URL query or cookies.
 * The codes are stored in cookies.
 */
export const TrackingCodesProvider = ({
  children,
  serverData,
}: {
  children: ReactNode
  serverData?: Record<string, string>
}) => {
  const [royaltyCode, setRoyaltyCode] = useState('')
  const [webSourceCode, setWebSourceCode] = useState('')
  const [isRecaptchaEnabled, setIsRecaptchaEnabled] = useState(true)
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const { isPageVisible } = usePageVisibility()
  const { cart } = useCart()
  const { order } = useOrderContext()

  const irclickidCodeFromQuery = searchParams?.get(QUERY_PARAMS.affiliateClickId)
  const irclickidCodeFromCookie = getBrowserCookie(COOKIES.affiliateClickId)
  const irclickidCodeResolved = irclickidCodeFromQuery ?? irclickidCodeFromCookie
  const [affiliateClickId, setAffiliateClickId] = useState(irclickidCodeResolved)

  const slAmbassadorIdFromQuery = searchParams?.get(QUERY_PARAMS.socialladderAmbassadorId)
  const slAmbassadorIdFromCookie = getBrowserCookie(COOKIES.socialladderAmbassadorId)
  const [socialladderAmbassadorId, setSocialladderAmbassadorId] = useState(slAmbassadorIdFromCookie)
  const [loadSocialLadderAttempts, setLoadSocialLadderAttempts] = useState(0)
  const [loadSocialLadderCompleted, setLoadSocialLadderCompleted] = useState(false)
  const shouldLoadSocialladderAmbassadorId =
    slAmbassadorIdFromQuery && !loadSocialLadderCompleted && loadSocialLadderAttempts < 5

  useMarketingAttribution(QUERY_PARAMS.nextDoorClickId)

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  useIdentify({
    'affiliate click id':
      irclickidCodeResolved ||
      cart?.custom?.fields?.affiliateClickId ||
      order?.custom?.fields?.affiliateClickId ||
      'EMPTY',
    'socialladder ambassador id':
      socialladderAmbassadorId ||
      cart?.custom?.fields?.socialladderAmbassadorId ||
      order?.custom?.fields?.socialladderAmbassadorId ||
      'EMPTY',
  })

  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  const loadAffiliateClickId = useCallback(() => {
    if (!irclickidCodeResolved) return
    if (irclickidCodeResolved !== irclickidCodeFromCookie)
      ServerActions.serial.setCookie(COOKIES.affiliateClickId, irclickidCodeResolved, {
        path: '/',
        maxAge: Number(configPublic.affiliate.cookieMaxAge),
      })
    setAffiliateClickId(irclickidCodeResolved)
  }, [irclickidCodeFromCookie, irclickidCodeResolved])

  const loadSocialladderAmbassadorId = useCallback(() => {
    if (!shouldLoadSocialladderAmbassadorId) return
    setLoadSocialLadderAttempts((prev) => prev + 1)
    ServerActions.parallel
      .getInviteToBuyGUID(slAmbassadorIdFromQuery)
      .then(({ inviteToBuyGUID }) => {
        if (!inviteToBuyGUID) return
        setLoadSocialLadderCompleted(true)
        setSocialladderAmbassadorId(inviteToBuyGUID)
        setBrowserCookie(
          COOKIES.socialladderAmbassadorId,
          inviteToBuyGUID,
          `path=/; max-age=${configPublic.socialLadder.cookieMaxAge}`
        )
      })
  }, [shouldLoadSocialladderAmbassadorId, slAmbassadorIdFromQuery])

  /** Loads the socialladder ambassador id every 5 seconds if it is not already loaded. */
  useInterval(loadSocialladderAmbassadorId, shouldLoadSocialladderAmbassadorId ? 5000 : null)

  const loadRoyaltyCode = useCallback(() => {
    const codeFromServer = serverData?.royaltyCode
    const codeFromQuery =
      searchParams?.get(QUERY_PARAMS.royaltyCodeAlt) ?? searchParams?.get(COOKIES.royaltyCode)
    const codeFromCookie = getBrowserCookie(COOKIES.royaltyCode)
    const codeResolved = codeFromQuery ?? codeFromServer ?? codeFromCookie
    if (!codeResolved) return
    if (codeResolved !== codeFromCookie) {
      const cookieOptions = `path=/; max-age=${ONE_DAY_IN_SECONDS * 30}`
      setBrowserCookie(COOKIES.royaltyCode, codeResolved, cookieOptions)
    }
    setRoyaltyCode(codeResolved)
  }, [searchParams, serverData?.royaltyCode])

  const loadWebSourceCode = useCallback(() => {
    const codeFromQuery =
      searchParams?.get(QUERY_PARAMS.webSourceCodeAlt) ?? searchParams?.get(COOKIES.webSourceCode)
    const codeFromCookie = getBrowserCookie(COOKIES.webSourceCode)
    const webSourceCodesSet = new WebSourceCodesSet(codeFromCookie)
    // add affiliate code to webSourceCode
    const hasAffiliateQueryParam = searchParams?.has(QUERY_PARAMS.affiliateClickId)
    if (hasAffiliateQueryParam) webSourceCodesSet.add(configPublic.affiliate.webSourceCode)
    webSourceCodesSet.add(codeFromQuery)
    const codeResolved = webSourceCodesSet.serialize()
    if (!codeResolved) return
    if (codeResolved !== codeFromCookie) setBrowserCookie(COOKIES.webSourceCode, codeResolved)
    setWebSourceCode(codeResolved)
  }, [searchParams])

  const loadRecaptchaEnabled = useCallback(() => {
    const codeFromQuery = searchParams?.get(COOKIES.recaptcha)
    const codeFromCookie = getBrowserCookie(COOKIES.recaptcha)
    const codeResolved = codeFromQuery ?? codeFromCookie
    if (!codeResolved) return
    if (codeResolved !== codeFromCookie) setBrowserCookie(COOKIES.recaptcha, codeResolved)
    setIsRecaptchaEnabled(codeResolved !== 'false')
  }, [searchParams])

  useEffect(() => {
    if (isPageVisible) {
      loadRoyaltyCode()
      loadWebSourceCode()
      loadRecaptchaEnabled()
      loadAffiliateClickId()
      loadSocialladderAmbassadorId()
    }
  }, [
    isPageVisible,
    loadRecaptchaEnabled,
    loadRoyaltyCode,
    loadWebSourceCode,
    loadAffiliateClickId,
    loadSocialladderAmbassadorId,
  ])

  // set webSourceCode in Cart
  useEffect(() => {
    if (!cart || !webSourceCode) return
    if (cart.custom?.fields?.webSourceCode !== webSourceCode) {
      void ServerActions.serial.setCustomFieldToCart({
        name: 'webSourceCode',
        value: webSourceCode,
      })
    }
  }, [cart, webSourceCode])

  // set affiliateClickId in Cart
  useEffect(() => {
    if (!cart || !affiliateClickId) return
    if (cart.custom?.fields?.affiliateClickId !== affiliateClickId) {
      void ServerActions.serial.setCustomFieldToCart({
        name: 'affiliateClickId',
        value: affiliateClickId,
      })
    }
  }, [cart, affiliateClickId])

  // set socialladderAmbassadorId in Cart
  useEffect(() => {
    if (!cart || !socialladderAmbassadorId) return
    if (cart.custom?.fields?.socialladderAmbassadorId !== socialladderAmbassadorId) {
      void ServerActions.serial.setCustomFieldToCart({
        name: 'socialladderAmbassadorId',
        value: socialladderAmbassadorId,
      })
    }
  }, [cart, socialladderAmbassadorId])

  // Delete webSourceCode, royaltyCode, recaptcha cookies when on order-confirmation page
  useEffect(() => {
    const unlocalizedPath = getUnlocalizedPath(pathname)
    if (unlocalizedPath === '/order-confirmation') {
      const webSourceCodeCookie = getBrowserCookie(COOKIES.webSourceCode)
      const royaltyCodeCookie = getBrowserCookie(COOKIES.royaltyCode)
      const recaptchaCookie = getBrowserCookie(COOKIES.recaptcha)
      if (webSourceCodeCookie) {
        removeBrowserCookie(COOKIES.webSourceCode)
        setWebSourceCode('')
        log.info(`${logNampsace} Deleted web-source-code cookie`)
      }
      if (royaltyCodeCookie) {
        removeBrowserCookie(COOKIES.royaltyCode)
        setRoyaltyCode('')
        log.info(`${logNampsace} Deleted royalty-code cookie`)
      }
      if (recaptchaCookie) {
        removeBrowserCookie(COOKIES.recaptcha)
        setIsRecaptchaEnabled(false)
        log.info(`${logNampsace} Deleted recaptcha cookie`)
      }
    }
  }, [pathname])

  useEffect(() => {
    log.info(`${logNampsace} STATUS`, {
      royaltyCode,
      webSourceCode,
      affiliateClickId,
      socialladderAmbassadorId,
      isRecaptchaEnabled,
    })
  }, [royaltyCode, webSourceCode, affiliateClickId, socialladderAmbassadorId, isRecaptchaEnabled])

  return (
    <TrackingCodesContext.Provider
      value={{
        royaltyCode,
        setRoyaltyCode,
        affiliateClickId,
        setAffiliateClickId,
        socialladderAmbassadorId,
        setSocialladderAmbassadorId,
        webSourceCode,
        setWebSourceCode,
        isRecaptchaEnabled,
        setIsRecaptchaEnabled,
      }}
    >
      {children}
    </TrackingCodesContext.Provider>
  )
}

/** This hook returns the tracking codes used in the app. */
export const useTrackingCodes = () => useContext(TrackingCodesContext)
